// =============================
// File: src/pages/admin/ClientDetail.tsx
// =============================
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiGrid, FiList, FiAlertCircle, FiCheck, FiUserPlus, FiSend, FiUser, FiUserCheck, FiTrash2 } from "react-icons/fi";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { useCustomerId } from "../../hooks/useCustomerId";
import { ProjectTile } from "../../features/projects/domain/types";
import { Customer, CustomerUpdate, CustomerStatus, PlanType } from "../../features/customers/domain/types";
import { ProjectCreateModal } from "../../features/projects/components/ProjectCreateModal";
import { ProjectsList } from "../../features/projects/components/ProjectsList";
import { ProjectGrid } from "../../features/projects/components/ProjectGrid";
import { Modal } from "../../features/common/components/Modal";
import { ModalButtons } from "../../features/common/components/ModalButtons";
import { UsageMeter } from "../../features/customers/components/UsageMeter";
import { getByCustomer } from "../../api/projects";
import { getById, getStatus, update, deleteCustomer } from "../../api/customers";
import { API } from "../../api/api";

interface UserListItem {
  user_id: string;
  email: string;
  username: string;
  full_name: string | null;
  customer_id: string | null;
  is_admin: boolean;
  is_active: boolean;
  needs_password_reset: boolean;
  created_at: string | null;
}

type ViewMode = "tiles" | "list";

const PLAN_OPTIONS: { value: PlanType; label: string }[] = [
  { value: "basic", label: "Basic" },
  { value: "premium", label: "Premium" },
  { value: "enterprise", label: "Enterprise" },
];

export default function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const customerIdFromJwt = useCustomerId(token);

  // UI State
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Edit form state
  const [editForm, setEditForm] = useState<CustomerUpdate>({});

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");

  // Delete customer state
  const [isDeleteCustomerOpen, setIsDeleteCustomerOpen] = useState(false);
  const [deleteCustomerConfirm, setDeleteCustomerConfirm] = useState("");

  // Resend/delete invite state
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Fetch customer details
  const {
    data: customer,
    isLoading: customerLoading,
    error: customerError,
  } = useQuery<Customer>({
    queryKey: ["customer", clientId],
    queryFn: async () => {
      if (!clientId || !token) throw new Error("Missing client ID or token");
      return await getById(clientId, token);
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!clientId && !!token,
  });

  // Fetch customer status (usage/gating)
  const {
    data: customerStatus,
    isLoading: statusLoading,
  } = useQuery<CustomerStatus | null>({
    queryKey: ["customerStatus", clientId],
    queryFn: async () => {
      if (!clientId || !token) return null;
      return await getStatus(clientId, token);
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!clientId && !!token,
  });

  // Fetch projects
  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useQuery<ProjectTile[]>({
    queryKey: ["projects", clientId],
    queryFn: async () => {
      if (!clientId || !token) return [];
      const data = await getByCustomer(clientId, token);
      const projectsArray = Array.isArray(data) ? data : data.projects || [];
      return projectsArray.map((proj: any) => ({
        project_id: proj.project_id,
        name: proj.project_name ?? `Project ${proj.project_id}`,
        status: proj.existing_project_flag ? "Operating" : "Prospective",
        config_version: proj.config_version || "v1.0",
        tech_type: proj.tech_type_id ?? undefined,
        mw_capacity: typeof proj.nameplate_capacity === "number"
          ? proj.nameplate_capacity
          : (proj.nameplate_capacity != null ? parseFloat(String(proj.nameplate_capacity)) : undefined),
        eia_ba_code: proj.eia_ba_code ?? undefined,
        cod_date: proj.cod_date ?? null,
        decommision_date: proj.decommision_date ?? null,
        latest_run_status: proj.latest_run_status ?? null,
        latest_run_id: proj.latest_run_id ?? null,
        latest_run_start_date: proj.latest_run_start_date ?? null,
        latest_run_end_date: proj.latest_run_end_date ?? null,
      }));
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!clientId && !!token,
  });

  // Fetch users for this customer
  const {
    data: clientUsers = [],
    isLoading: usersLoading,
  } = useQuery<UserListItem[]>({
    queryKey: ["users", clientId],
    queryFn: async () => {
      if (!token) return [];
      const res = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) return [];
      const allUsers: UserListItem[] = await res.json();
      return allUsers.filter(u => u.customer_id === clientId);
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!clientId && !!token,
  });

  // Resend invite handler
  const handleResendInvite = async (userId: string) => {
    setResendingId(userId);
    setResendSuccess(null);
    try {
      const res = await fetch(`${API}/admin/resend-invite/${userId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: `Resend failed: ${res.status}` }));
        throw new Error(data.detail || `Resend failed: ${res.status}`);
      }
      setResendSuccess(userId);
      setTimeout(() => setResendSuccess(null), 3000);
      queryClient.invalidateQueries({ queryKey: ["users", clientId] });
    } catch (err) {
      setErrorMsg((err as Error).message || "Failed to resend invite");
      setTimeout(() => setErrorMsg(null), 5000);
    } finally {
      setResendingId(null);
    }
  };

  // Delete user handler
  const handleDeleteUser = async (userId: string) => {
    setDeletingId(userId);
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: `Delete failed: ${res.status}` }));
        throw new Error(data.detail || `Delete failed: ${res.status}`);
      }
      queryClient.invalidateQueries({ queryKey: ["users", clientId] });
      setSuccessMsg("User deleted");
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setErrorMsg((err as Error).message || "Failed to delete user");
      setTimeout(() => setErrorMsg(null), 5000);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  // Update customer mutation
  const updateMutation = useMutation({
    mutationFn: async (data: CustomerUpdate) => {
      if (!clientId || !token) throw new Error("Missing client ID or token");
      return await update(clientId, data, token);
    },
    onSuccess: (updatedCustomer) => {
      queryClient.setQueryData(["customer", clientId], updatedCustomer);
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customerStatus", clientId] });

      setIsEditOpen(false);
      setSuccessMsg("Customer settings updated successfully");
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    onError: (error: Error) => {
      setErrorMsg(error.message || "Failed to update customer");
      setTimeout(() => setErrorMsg(null), 5000);
    },
  });

  // Invite user mutation
  const inviteMutation = useMutation({
    mutationFn: async ({ email, fullName }: { email: string; fullName: string }) => {
      if (!clientId || !token) throw new Error("Missing client ID or token");
      const res = await fetch(`${API}/admin/invite-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_id: clientId,
          email,
          full_name: fullName || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || `Invite failed: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      setIsInviteOpen(false);
      setInviteEmail("");
      setInviteName("");
      queryClient.invalidateQueries({ queryKey: ["users", clientId] });
      setSuccessMsg("Invite sent successfully");
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    onError: (error: Error) => {
      setErrorMsg(error.message || "Failed to send invite");
      setTimeout(() => setErrorMsg(null), 5000);
    },
  });

  // Delete customer mutation
  const deleteCustomerMutation = useMutation({
    mutationFn: async () => {
      if (!clientId || !token || !customer) throw new Error("Missing data");
      return await deleteCustomer(clientId, customer.customer_name, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setIsDeleteCustomerOpen(false);
      nav("/admin/clients");
    },
    onError: (error: Error) => {
      setErrorMsg(error.message || "Failed to delete customer");
      setTimeout(() => setErrorMsg(null), 5000);
    },
  });

  // Open edit modal with current values
  const handleOpenEdit = () => {
    if (customer) {
      setEditForm({
        customer_name: customer.customer_name,
        contact_email: customer.contact_email || "",
        plan_type: customer.plan_type,
        monthly_run_limit: customer.monthly_run_limit,
        monthly_mw_limit: customer.monthly_mw_limit,
        mw_throttle_limit: customer.mw_throttle_limit,
        gating_disabled: customer.gating_disabled,
        require_2fa: customer.require_2fa ?? false,
        social_discount_rate: customer.social_discount_rate ?? 0.03,
      });
    }
    setIsEditOpen(true);
  };

  // Handle edit form submission
  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(editForm);
  };

  // Handle project creation
  const handleProjectCreated = (projectId: string, projectName: string) => {
    queryClient.setQueryData(["projects", clientId], (old: ProjectTile[] = []) => [
      ...old,
      { project_id: projectId, name: projectName, status: "Prospective" },
    ]);
    nav(
      `/admin/projects/${encodeURIComponent(projectName)}?client=${encodeURIComponent(
        customer?.customer_name || ""
      )}&project_id=${encodeURIComponent(projectId)}&client_id=${encodeURIComponent(clientId || "")}`
    );
  };

  // Handle project click
  const handleProjectClick = (project: ProjectTile) => {
    nav(
      `/admin/projects/${encodeURIComponent(project.name)}?client=${encodeURIComponent(
        customer?.customer_name || ""
      )}&project_id=${encodeURIComponent(project.project_id)}&client_id=${encodeURIComponent(clientId || "")}`
    );
  };

  const handleListProjectClick = (projectId: string, projectName: string) => {
    nav(
      `/admin/projects/${encodeURIComponent(projectName)}?client=${encodeURIComponent(
        customer?.customer_name || ""
      )}&project_id=${encodeURIComponent(projectId)}&client_id=${encodeURIComponent(clientId || "")}`
    );
  };

  if (customerLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-600 rounded w-1/4"></div>
          <div className="h-32 bg-surface-600 rounded"></div>
        </div>
      </div>
    );
  }

  if (customerError) {
    return (
      <div className="p-6">
        <div className="bg-health-500/10 border border-health-500/30 rounded-lg p-4 text-health-400">
          Error: {(customerError as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Link to="/admin/clients" className="text-body-sm text-quantum-400 hover:text-quantum-300 hover:underline">
        ← Back to Customers
      </Link>

      {/* Success/Error Messages */}
      {successMsg && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-quantum-500/10 border border-quantum-500/30 px-4 py-3 text-quantum-400">
          <FiCheck />
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-health-500/10 border border-health-500/30 px-4 py-3 text-health-400">
          <FiAlertCircle />
          {errorMsg}
        </div>
      )}

      <div className="mt-4 mb-6 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-h2 text-text-primary">{customer?.customer_name}</h1>
          {clientId && (
            <span className="rounded bg-surface-600 px-2 py-0.5 text-caption text-text-muted border border-border">
              ID: {clientId.slice(0, 8)}...
            </span>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Client Information Card */}
        <div className="card">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-h4 text-text-primary">Customer Information</h2>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsInviteOpen(true)}
                className="flex items-center gap-1.5 text-body-sm text-quantum-400 hover:text-quantum-300 hover:underline"
              >
                <FiUserPlus className="w-4 h-4" /> Invite User
              </button>
              <button
                onClick={handleOpenEdit}
                className="text-body-sm text-quantum-400 hover:text-quantum-300 hover:underline"
              >
                Edit Settings
              </button>
              <button
                onClick={() => { setDeleteCustomerConfirm(""); setIsDeleteCustomerOpen(true); }}
                className="flex items-center gap-1.5 text-body-sm text-health-400 hover:text-health-300 hover:underline"
              >
                <FiTrash2 className="w-4 h-4" /> Delete Customer
              </button>
            </div>
          </div>

          {customer && (
            <div className="space-y-6">
              {/* Basic Info */}
              <section className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <h3 className="mb-1 text-caption font-medium text-text-secondary">Email</h3>
                  <p className="text-body-sm text-text-primary">{customer.contact_email || "—"}</p>
                </div>
                <div>
                  <h3 className="mb-1 text-caption font-medium text-text-secondary">Plan Type</h3>
                  <span className="inline-block rounded bg-quantum-500/20 px-2 py-0.5 text-caption font-medium text-quantum-400 capitalize">
                    {customer.plan_type}
                  </span>
                </div>
                <div>
                  <h3 className="mb-1 text-caption font-medium text-text-secondary">Per-Project Limit</h3>
                  <p className="text-body-sm text-text-primary">{customer.mw_throttle_limit} MW</p>
                </div>
                <div>
                  <h3 className="mb-1 text-caption font-medium text-text-secondary">Gating</h3>
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-caption font-medium ${
                      customer.gating_disabled
                        ? "bg-economic-500/20 text-economic-400"
                        : "bg-ecosystem-500/20 text-ecosystem-400"
                    }`}
                  >
                    {customer.gating_disabled ? "Disabled (Unlimited)" : "Enabled"}
                  </span>
                </div>
              </section>

              {/* Lifetime Analysis Settings */}
              <section className="border-t border-border pt-4">
                <h3 className="mb-3 text-body font-medium text-text-primary">Lifetime Analysis Settings</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-body-sm">
                  <div className="rounded-lg bg-surface-600 p-3">
                    <p className="text-text-muted">Social Discount Rate</p>
                    <p className="text-h4 text-text-primary">{((customer.social_discount_rate ?? 0.03) * 100).toFixed(1)}%</p>
                    <p className="text-caption text-text-muted mt-1">For NPV calculations</p>
                  </div>
                </div>
              </section>

              {/* Usage Section */}
              <section className="border-t border-border pt-4">
                <h3 className="mb-3 text-body font-medium text-text-primary">Usage</h3>

                {statusLoading ? (
                  <p className="text-body-sm text-text-muted">Loading usage data...</p>
                ) : customerStatus && !customerStatus.gating_disabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <UsageMeter
                      used={customerStatus.runs_used}
                      limit={customerStatus.monthly_run_limit}
                      label="Monthly Runs"
                    />
                    <UsageMeter
                      used={customerStatus.mw_used}
                      limit={customerStatus.monthly_mw_limit}
                      label="Monthly MW"
                      unit="MW"
                    />
                  </div>
                ) : (
                  <p className="text-body-sm text-text-muted">
                    Usage limits are disabled for this customer.
                  </p>
                )}
              </section>

              {/* Limits Overview */}
              <section className="border-t border-border pt-4">
                <h3 className="mb-3 text-body font-medium text-text-primary">Limits Configuration</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-body-sm">
                  <div className="rounded-lg bg-surface-600 p-3">
                    <p className="text-text-muted">Monthly Runs</p>
                    <p className="text-h4 text-text-primary">{customer.monthly_run_limit}</p>
                  </div>
                  <div className="rounded-lg bg-surface-600 p-3">
                    <p className="text-text-muted">Monthly MW</p>
                    <p className="text-h4 text-text-primary">{customer.monthly_mw_limit}</p>
                  </div>
                  <div className="rounded-lg bg-surface-600 p-3">
                    <p className="text-text-muted">Existing MW Limit</p>
                    <p className="text-h4 text-text-primary">{customer.monthly_existing_mw_limit}</p>
                  </div>
                  <div className="rounded-lg bg-surface-600 p-3">
                    <p className="text-text-muted">Future Runs Limit</p>
                    <p className="text-h4 text-text-primary">{customer.monthly_future_run_limit}</p>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>

        {/* Users Section */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-h4 text-text-primary">Users ({clientUsers.length})</h2>
            <button
              onClick={() => setIsInviteOpen(true)}
              className="flex items-center gap-1.5 text-body-sm text-quantum-400 hover:text-quantum-300 hover:underline"
            >
              <FiUserPlus className="w-4 h-4" /> Invite User
            </button>
          </div>

          {usersLoading ? (
            <div className="space-y-2">
              {[1, 2].map(i => (
                <div key={i} className="h-10 bg-surface-600 animate-pulse rounded" />
              ))}
            </div>
          ) : clientUsers.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="grid grid-cols-12 bg-surface-600 px-4 py-2 text-caption font-medium text-text-secondary">
                <div className="col-span-4">Email</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Status</div>
                <div className="col-span-2">Actions</div>
              </div>
              <div className="divide-y divide-border">
                {clientUsers.map(user => (
                  <div key={user.user_id} className="grid grid-cols-12 px-4 py-3 items-center text-body-sm">
                    <div className="col-span-4 flex items-center gap-2 text-text-primary truncate">
                      {user.is_admin ? <FiUserCheck className="text-quantum-400 shrink-0" /> : <FiUser className="text-text-muted shrink-0" />}
                      <span className="truncate">{user.email}</span>
                    </div>
                    <div className="col-span-3 text-text-secondary truncate">{user.full_name || "—"}</div>
                    <div className="col-span-3">
                      {!user.is_active ? (
                        <span className="px-2 py-1 rounded text-caption font-medium bg-surface-500 text-text-muted">Inactive</span>
                      ) : user.needs_password_reset ? (
                        <span className="px-2 py-1 rounded text-caption font-medium bg-economic-500/20 text-economic-400">Pending Invite</span>
                      ) : (
                        <span className="px-2 py-1 rounded text-caption font-medium bg-quantum-500/20 text-quantum-400">Active</span>
                      )}
                    </div>
                    <div className="col-span-2">
                      {user.needs_password_reset && user.is_active && (
                        <button
                          onClick={() => handleResendInvite(user.user_id)}
                          disabled={resendingId === user.user_id}
                          className="flex items-center gap-1 text-caption text-quantum-400 hover:text-quantum-300 disabled:opacity-50"
                          title="Resend invite email"
                        >
                          <FiSend className="w-3.5 h-3.5" />
                          {resendingId === user.user_id ? "..." : resendSuccess === user.user_id ? "Sent!" : "Resend"}
                        </button>
                      )}
                      {confirmDeleteId === user.user_id ? (
                        <span className="flex items-center gap-1">
                          <button
                            onClick={() => handleDeleteUser(user.user_id)}
                            disabled={deletingId === user.user_id}
                            className="text-caption text-health-400 hover:text-health-300 disabled:opacity-50"
                          >
                            {deletingId === user.user_id ? "..." : "Confirm"}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-caption text-text-muted hover:text-text-secondary"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(user.user_id)}
                          className="text-text-muted hover:text-health-400"
                          title="Delete user"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-body-sm text-text-muted">No users yet. Click "Invite User" to add one.</p>
          )}
        </div>

        {/* Projects Section */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-h4 text-text-primary">Projects ({projects.length})</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsAddOpen(true)}
                className="px-3 py-1.5 text-body-sm font-medium rounded-lg text-quantum-400 border border-quantum-500/40 bg-quantum-500/10 hover:bg-quantum-500/20 transition-colors"
              >
                + New Project
              </button>
              <div className="bg-surface-600 rounded-lg p-1 flex">
                <button
                  onClick={() => setViewMode("tiles")}
                  className={`px-3 py-1.5 rounded-md text-body-sm flex items-center transition-colors ${
                    viewMode === "tiles"
                      ? "bg-surface-500 text-quantum-400"
                      : "text-text-muted hover:text-text-primary"
                  }`}
                >
                  <FiGrid className="mr-1.5" /> Tiles
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-1.5 rounded-md text-body-sm flex items-center transition-colors ${
                    viewMode === "list"
                      ? "bg-surface-500 text-quantum-400"
                      : "text-text-muted hover:text-text-primary"
                  }`}
                >
                  <FiList className="mr-1.5" /> List
                </button>
              </div>
            </div>
          </div>

          {projectsError ? (
            <p className="text-body-sm text-health-400">{(projectsError as Error).message}</p>
          ) : viewMode === "list" ? (
            <ProjectsList
              projects={projects}
              isLoading={projectsLoading}
              onProjectClick={handleListProjectClick}
              onCreateClick={() => setIsAddOpen(true)}
              showCreateItem={true}
              customerId={clientId || ""}
            />
          ) : (
            <ProjectGrid
              projects={projects}
              onProjectClick={handleProjectClick}
              onCreateClick={() => setIsAddOpen(true)}
              isLoading={projectsLoading}
              error={projectsError as Error | null}
            />
          )}
        </div>
      </div>

      {/* Project Create Modal */}
      <ProjectCreateModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        customerId={clientId || customerIdFromJwt || ""}
        afterCreate={handleProjectCreated}
        title={`New Project for ${customer?.customer_name}`}
      />

      {/* Invite User Modal */}
      {isInviteOpen && (
        <Modal title={`Invite User to ${customer?.customer_name}`} onClose={() => setIsInviteOpen(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              inviteMutation.mutate({ email: inviteEmail, fullName: inviteName });
            }}
            className="space-y-5"
          >
            <div>
              <label className="input-label">Email Address</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="input"
                placeholder="user@company.com"
                required
              />
            </div>
            <div>
              <label className="input-label">Full Name (optional)</label>
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                className="input"
                placeholder="Jane Smith"
              />
            </div>
            <p className="text-caption text-text-muted">
              An invite email will be sent with a link to set their password.
            </p>
            <ModalButtons
              onCancel={() => setIsInviteOpen(false)}
              submitLabel={inviteMutation.isPending ? "Sending..." : "Send Invite"}
              disabled={inviteMutation.isPending}
            />
          </form>
        </Modal>
      )}

      {/* Edit Customer Modal */}
      {isEditOpen && (
        <Modal title="Edit Customer Settings" onClose={() => setIsEditOpen(false)}>
          <form onSubmit={handleEditSubmit} className="space-y-5">
            {/* Customer Name */}
            <div>
              <label className="input-label">Customer Name</label>
              <input
                type="text"
                value={editForm.customer_name || ""}
                onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                className="input"
                placeholder="Customer name"
              />
            </div>

            {/* Contact Email */}
            <div>
              <label className="input-label">Contact Email</label>
              <input
                type="email"
                value={editForm.contact_email || ""}
                onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                className="input"
                placeholder="contact@company.com"
              />
            </div>

            {/* Plan Type */}
            <div>
              <label className="input-label">Plan Type</label>
              <select
                value={editForm.plan_type || "basic"}
                onChange={(e) => setEditForm({ ...editForm, plan_type: e.target.value })}
                className="input"
              >
                {PLAN_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Monthly Run Limit</label>
                <input
                  type="number"
                  min={0}
                  value={editForm.monthly_run_limit ?? ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, monthly_run_limit: parseInt(e.target.value) || 0 })
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="input-label">Monthly MW Limit</label>
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  value={editForm.monthly_mw_limit ?? ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, monthly_mw_limit: parseFloat(e.target.value) || 0 })
                  }
                  className="input"
                />
              </div>
            </div>

            {/* Per-Project Throttle */}
            <div>
              <label className="input-label">
                Per-Project MW Limit (Throttle)
              </label>
              <input
                type="number"
                min={0}
                step="0.1"
                value={editForm.mw_throttle_limit ?? ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, mw_throttle_limit: parseFloat(e.target.value) || 0 })
                }
                className="input"
              />
              <p className="mt-1 text-caption text-text-muted">
                Maximum MW allowed per individual project
              </p>
            </div>

            {/* Social Discount Rate */}
            <div>
              <label className="input-label">
                Social Discount Rate (%)
              </label>
              <input
                type="number"
                min={0}
                max={20}
                step="0.1"
                value={((editForm.social_discount_rate ?? 0.03) * 100).toFixed(1)}
                onChange={(e) =>
                  setEditForm({ ...editForm, social_discount_rate: parseFloat(e.target.value) / 100 || 0.03 })
                }
                className="input"
              />
              <p className="mt-1 text-caption text-text-muted">
                Used for NPV calculations in lifetime impact projections (typical range: 1-7%)
              </p>
            </div>

            {/* Gating Toggle */}
            <div className="flex items-center justify-between rounded-lg border border-border bg-surface-600 p-4">
              <div>
                <p className="font-medium text-text-primary">Disable Usage Gating</p>
                <p className="text-body-sm text-text-secondary">
                  Allow unlimited usage (no monthly limits enforced)
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditForm({ ...editForm, gating_disabled: !editForm.gating_disabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editForm.gating_disabled ? "bg-economic-500" : "bg-surface-500"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editForm.gating_disabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {editForm.gating_disabled && (
              <div className="flex items-center gap-2 rounded-lg bg-economic-500/10 border border-economic-500/30 px-3 py-2 text-body-sm text-economic-400">
                <FiAlertCircle />
                Warning: This customer will have unlimited usage.
              </div>
            )}

            {/* Require 2FA Toggle */}
            <div className="flex items-center justify-between rounded-lg border border-border bg-surface-600 p-4">
              <div>
                <p className="font-medium text-text-primary">Require Two-Factor Authentication</p>
                <p className="text-body-sm text-text-secondary">
                  All users must enable 2FA to access their accounts
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditForm({ ...editForm, require_2fa: !editForm.require_2fa })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editForm.require_2fa ? "bg-quantum-500" : "bg-surface-500"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editForm.require_2fa ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <ModalButtons
              onCancel={() => setIsEditOpen(false)}
              submitLabel={updateMutation.isPending ? "Saving..." : "Save Changes"}
              disabled={updateMutation.isPending}
            />
          </form>
        </Modal>
      )}

      {/* Delete Customer Confirmation Modal */}
      {isDeleteCustomerOpen && customer && (
        <Modal title="Delete Customer" onClose={() => !deleteCustomerMutation.isPending && setIsDeleteCustomerOpen(false)}>
          <div className="space-y-4">
            <div className="rounded-lg bg-health-500/10 border border-health-500/30 p-4 text-body-sm text-health-400">
              <p className="font-medium">This action cannot be undone.</p>
              <p className="mt-2">
                This will permanently delete <strong>{customer.customer_name}</strong> along with:
              </p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>{projects.length} project{projects.length !== 1 ? "s" : ""} and all associated model runs</li>
                <li>{clientUsers.length} user{clientUsers.length !== 1 ? "s" : ""}</li>
              </ul>
            </div>

            <div>
              <label className="input-label">
                Type <strong>{customer.customer_name}</strong> to confirm
              </label>
              <input
                type="text"
                value={deleteCustomerConfirm}
                onChange={(e) => setDeleteCustomerConfirm(e.target.value)}
                className="input"
                placeholder={customer.customer_name}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteCustomerOpen(false)}
                className="btn-secondary"
                disabled={deleteCustomerMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteCustomerMutation.mutate()}
                disabled={deleteCustomerConfirm !== customer.customer_name || deleteCustomerMutation.isPending}
                className="rounded-lg bg-health-500 px-4 py-2 text-body-sm font-medium text-white hover:bg-health-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteCustomerMutation.isPending ? "Deleting..." : "Delete Customer"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
